import React, { useState, useEffect } from 'react';
import {
    AppBar,
    Toolbar,
    Box,
    Button,
    Container,
    Typography,
    Avatar,
    alpha,
    useTheme,
    Menu,
    MenuItem,
    ListItemIcon,
    Divider,
    Tooltip,
    Stack,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Logout as LogoutIcon,
    KeyboardArrowDown as ArrowDownIcon,
    Menu as MenuIcon,
    Close as CloseIcon
} from '@mui/icons-material';

import { ReactComponent as DashboardIcon } from '../Assests/dashboard.svg';
import { ReactComponent as JobIcon } from '../Assests/job.svg';
import { ReactComponent as StudentIcon } from '../Assests/student.svg';
import { ReactComponent as CollageIcon } from '../Assests/collage.svg';
import avm from '../Assests/avm.svg';
import { getMyCompanyDetails } from '../Apis/CompanyApis';
import { useAuth } from '../Context/AuthContext';

const Navbar = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();
    const [logo, setLogo] = useState(avm);
    const [anchorEl, setAnchorEl] = useState(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const open = Boolean(anchorEl);

    const menuItems = [
        { text: 'Dashboard', icon: <DashboardIcon />, path: '/', type: 'stroke' },
        { text: 'Interactions', icon: <JobIcon />, path: '/job-drives', type: 'stroke' },
        { text: 'Candidates', icon: <StudentIcon />, path: '/applications', type: 'stroke' },
        { text: 'Intelligence', icon: <DashboardIcon />, path: '/reports', type: 'stroke' },
        { text: 'Partners', icon: <CollageIcon />, path: '/college-details', type: 'stroke' },
    ];

    useEffect(() => {
        const fetchBrandAssets = async () => {
            try {
                const response = await getMyCompanyDetails();
                if (response?.success && response.data?.logo) {
                    setLogo(response.data.logo);
                }
            } catch (error) {
                console.error("Failed to fetch company logo:", error);
            }
        };
        fetchBrandAssets();
    }, []);

    const handleProfileClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

    const drawer = (
        <Box sx={{ p: 2, height: '100%', bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4, px: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ p: 1, bgcolor: '#F8FAFC', borderRadius: 2, display: 'flex', alignItems: 'center' }}>
                        <img src={logo} alt="Logo" style={{ height: 24, width: 'auto' }} />
                    </Box>
                    <Typography variant="subtitle1" fontWeight="1000" color="#0F172A">SkillTech</Typography>
                </Box>
                <IconButton onClick={handleDrawerToggle} sx={{ color: '#64748B' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <List sx={{ px: 1 }}>
                {menuItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    const isStroke = item.type === 'stroke';

                    return (
                        <ListItem key={item.text} disablePadding sx={{ mb: 1 }}>
                            <ListItemButton
                                onClick={() => { navigate(item.path); handleDrawerToggle(); }}
                                sx={{
                                    borderRadius: 3,
                                    bgcolor: isActive ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                                    color: isActive ? '#2563EB' : '#64748B',
                                    '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
                                    py: 1.5
                                }}
                            >
                                <ListItemIcon sx={{
                                    minWidth: 40,
                                    '& svg': {
                                        width: 22,
                                        height: 22,
                                        '& path': {
                                            stroke: isStroke ? (isActive ? '#2563EB' : '#64748B') : 'none',
                                            fill: isStroke ? 'none' : (isActive ? '#2563EB' : '#64748B')
                                        }
                                    }
                                }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.text}
                                    primaryTypographyProps={{
                                        fontWeight: isActive ? '1000' : '700',
                                        fontSize: '0.95rem'
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    );
                })}
            </List>

            <Box sx={{ position: 'absolute', bottom: 20, left: 20, right: 20 }}>
                <Divider sx={{ mb: 2, borderStyle: 'dashed' }} />
                <Button
                    fullWidth
                    variant="soft"
                    color="error"
                    startIcon={<LogoutIcon />}
                    onClick={() => { logout(); navigate('/login'); }}
                    sx={{
                        bgcolor: alpha('#F43F5E', 0.1),
                        color: '#F43F5E',
                        fontWeight: '1000',
                        py: 1.5,
                        borderRadius: 3,
                        textTransform: 'none',
                        '&:hover': { bgcolor: alpha('#F43F5E', 0.2) }
                    }}
                >
                    Log Out
                </Button>
            </Box>
        </Box>
    );

    return (
        <AppBar
            elevation={0}
            position="sticky"
            sx={{
                bgcolor: alpha('#fff', 0.8),
                backdropFilter: 'blur(20px)',
                borderBottom: '1px solid #F1F5F9',
                top: 0,
                zIndex: theme.zIndex.drawer + 1
            }}
        >
            <Container maxWidth="xl">
                <Toolbar
                    disableGutters
                    sx={{
                        minHeight: { xs: 70, md: 80 },
                        display: 'flex',
                        justifyContent: 'space-between'
                    }}
                >
                    {/* Mobile Menu Icon */}
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{ mr: 2, display: { lg: 'none' }, color: '#64748B' }}
                    >
                        <MenuIcon />
                    </IconButton>

                    {/* Brand Section */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            cursor: 'pointer',
                            gap: 2,
                            flexGrow: { xs: 1, lg: 0 }
                        }}
                        onClick={() => navigate('/')}
                    >
                        <Box sx={{ p: 1, bgcolor: '#F8FAFC', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <img
                                src={logo}
                                alt="Logo"
                                style={{ height: 32, width: 'auto', objectFit: 'contain' }}
                            />
                        </Box>
                        <Typography variant="h6" fontWeight="1000" color="#0F172A" sx={{ display: { xs: 'none', sm: 'block' } }}>SkillTech</Typography>
                    </Box>


                    <Box
                        sx={{
                            display: { xs: 'none', lg: 'flex' },
                            alignItems: 'center',
                            gap: 1,
                            p: 0.5,
                            bgcolor: '#F8FAFC',
                            borderRadius: 4,
                        }}
                    >
                        {menuItems.map((item) => {
                            const isActive = location.pathname === item.path;
                            const isStroke = item.type === 'stroke';

                            return (
                                <Button
                                    key={item.text}
                                    onClick={() => navigate(item.path)}
                                    startIcon={item.icon}
                                    disableElevation
                                    sx={{
                                        color: isActive ? '#0F172A' : '#64748B',
                                        bgcolor: isActive ? 'white' : 'transparent',
                                        boxShadow: isActive ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                                        fontWeight: isActive ? '1000' : '700',
                                        textTransform: 'none',
                                        fontSize: '0.85rem',
                                        borderRadius: 3,
                                        px: 2.5,
                                        py: 1,
                                        '& svg': {
                                            width: 18,
                                            height: 18,
                                            '& path': {
                                                stroke: isStroke
                                                    ? isActive ? '#2563EB' : '#64748B'
                                                    : 'none',
                                                fill: isStroke
                                                    ? 'none'
                                                    : isActive ? '#2563EB' : '#64748B'
                                            }
                                        },
                                        '&:hover': {
                                            bgcolor: isActive ? 'white' : alpha(theme.palette.primary.main, 0.04),
                                            color: '#0F172A'
                                        }
                                    }}
                                >
                                    {item.text}
                                </Button>
                            );
                        })}
                    </Box>

                    {/* User Profile Section */}
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Tooltip title="Account settings">
                            <Box
                                onClick={handleProfileClick}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    p: 0.5,
                                    pr: { xs: 0, sm: 1.5 },
                                    borderRadius: 10,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    '&:hover': { bgcolor: '#F8FAFC' }
                                }}
                            >
                                <Avatar
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: '#2563EB',
                                        fontWeight: 'bold',
                                        fontSize: '0.9rem',
                                        boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)'
                                    }}
                                >
                                    {user?.user?.firstName?.[0] || ''}{user?.user?.lastName?.[0] || 'U'}
                                </Avatar>
                                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                    <Typography variant="caption" fontWeight="1000" color="#0F172A" display="block" sx={{ lineHeight: 1 }}>
                                        {user?.user?.firstName} {user?.user?.lastName}
                                    </Typography>
                                    <Typography variant="caption" color="#64748B" fontWeight="800">
                                        {user?.user?.company?.name || 'Industry Partner'}
                                    </Typography>
                                </Box>
                                <ArrowDownIcon sx={{ fontSize: 16, color: '#94A3B8', display: { xs: 'none', sm: 'block' } }} />
                            </Box>
                        </Tooltip>

                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            PaperProps={{
                                elevation: 0,
                                sx: {
                                    overflow: 'visible',
                                    filter: 'drop-shadow(0px 10px 20px rgba(0,0,0,0.08))',
                                    mt: 1.5,
                                    borderRadius: 4,
                                    minWidth: 200,
                                    border: '1px solid #F1F5F9',
                                    '&:before': {
                                        content: '""',
                                        display: 'block',
                                        position: 'absolute',
                                        top: 0,
                                        right: 14,
                                        width: 10,
                                        height: 10,
                                        bgcolor: 'background.paper',
                                        transform: 'translateY(-50%) rotate(45deg)',
                                        zIndex: 0,
                                    },
                                },
                            }}
                            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        >
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Typography variant="subtitle2" fontWeight="1000" color="#0F172A">Workspace</Typography>
                                <Typography variant="caption" color="#64748B">{user?.user?.company?.name || 'SkillTech Industry Connect'}</Typography>
                            </Box>
                            <Divider sx={{ borderStyle: 'dashed' }} />
                            <MenuItem onClick={() => { handleClose(); logout(); navigate('/login'); }} sx={{ py: 1, typography: 'body2', fontWeight: 'bold', color: '#F43F5E' }}>
                                <ListItemIcon><LogoutIcon fontSize="small" sx={{ color: '#F43F5E' }} /></ListItemIcon>
                                Log Out
                            </MenuItem>
                        </Menu>
                    </Stack>
                </Toolbar>
            </Container>

            {/* Mobile Drawer */}
            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', lg: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280, borderRight: 'none', boxShadow: '10px 0 20px rgba(0,0,0,0.05)' },
                }}
            >
                {drawer}
            </Drawer>
        </AppBar >
    );
};

export default Navbar;
